import React, { useEffect, useState } from 'react';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import Card from '../../components/cards/Card';
import CustomLoading from '../../components/customLoading/CustomLoading';
import Table from '../../components/tables/Table';
import Pagination from '../../components/tables/pagination/Pagination';
import SearchBar from '../../components/searchBar/SearchBar';
import DatePicker from '../../components/inputFields/DatePicker';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BiCopy, BiEdit, BiRefresh } from 'react-icons/bi';
import { getPayments, getPaymentStats } from '../../services/payment';
import { formatDateTime } from '../../utils/FormatDateTime';
import Dropdown from '../../components/inputFields/DropDown';
import BarChart from '../../components/BarChart';
import ShowModal from '../../utils/ShowModal';
import { patchOrderPayment } from '../../services/payment';
import * as Yup from "yup";
import { Form, Formik } from 'formik';
import InputField from '../../components/inputFields/InputField';
import FormikDropdown from '../../components/inputFields/FormikDropDown';
import Button from '../../components/buttons/Button';
import { getUserRole } from '../../helpers/local_storage_helpers';

const EditPayment = ({ item, onOperation }) => {
    const handleFormSubmit = async (values, { setSubmitting }) => {
        try {
            const response = await patchOrderPayment(item.id, values);
            if (response?.status === 200) {
                toast.success("Payment Updated successfully!");
                onOperation();
            } else {
                toast.error("Opps! Something went wrong, please try again.");
            }
        } catch (error) {
            toast.error(error?.response?.data?.error || "Opps! Something went wrong, please try again.");
        }
        setSubmitting(false);
    };

    return (
        <div className="w-full flex-1">
            <StyledHeadingText text={`Edit Payment (Order ID: ${item?.order})`} />
            <Formik
                initialValues={{
                    payment_status: item?.payment_status || "",
                    payment_mode: item?.payment_method || "",
                    payment_amount: item?.payment_amount || "",
                    payment_date: item?.payment_date ? item.payment_date.split('T')[0] : "",
                }}
                validationSchema={Yup.object({
                    payment_mode: Yup.string().required("Mode is required"),
                    payment_amount: Yup.string().required("Amount is required"),
                    payment_status: Yup.string().required("Status is required")
                })}
                onSubmit={handleFormSubmit}
            >
                {({ isSubmitting }) => (
                    <Form>
                        <div className="grid gap-2 grid-cols-1 md:grid-cols-2">
                            <FormikDropdown
                                label="Payment Mode"
                                name="payment_mode"
                                placeholder="Select Mode"
                                required={true}
                                options={[
                                    { label: "UPI", value: "UPI" },
                                    { label: "CARD", value: "CARD" },
                                    { label: "CASH", value: "CASH" },
                                    { label: "CHEQUE", value: "CHEQUE" },
                                    { label: "BANK_TRANSFER", value: "BANK_TRANSFER" },
                                    { label: "RAZORPAY", value: "Razorpay" },
                                    { label: "SBI_UPI", value: "SBI_UPI" },
                                    { label: "SARAL_UPI", value: "SARAL_UPI" }
                                ]}
                            />
                            <InputField
                                label="Payment Amount"
                                name="payment_amount"
                                placeholder="Enter Amount"
                                required
                            />
                            <InputField
                                label="Payment Date"
                                name="payment_date"
                                type="date"
                            />
                            <FormikDropdown
                                label="Status"
                                name="payment_status"
                                placeholder="Select Status"
                                required={true}
                                options={[
                                    { label: "Pending", value: "PENDING" },
                                    { label: "Completed", value: "COMPLETED" },
                                    { label: "Failed", value: "FAILED" }
                                ]} 
                            />
                        </div>
                        <div className="py-5 flex justify-end gap-6">
                            <Button
                                type="submit"
                                className="text-white bg-primary hover:bg-primary-dark font-sm rounded-lg text-lg px-8 py-1.5"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Updating..." : "Update"}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

function Payments() {
    const [cardData, setCardData] = useState([]);
    const [graphData, setGraphData] = useState({});
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const headers = [
        { label: "Date", key: "created_at" },
        { label: "Farmer Ph.", key: "farmer_mobile_number" },
        { label: "Farmer Name", key: "farmer_name" },
        { label: "Order ID", key: "order" },
        { label: "Order Value", key: "payment_amount" },
        { label: "Payment Method", key: "payment_method" },
        { label: "Status", key: "payment_status" },
    ];

    if (getUserRole() === "admin") {
        headers.push({ label: "Action", key: "action" });
    }

    const pageSize = 10;
    const [searchText, setSearchText] = useState("");
    const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
    const [paymentFilterStatus, setPaymentFilterStatus] = useState("");

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [openingItem, setOpeningItem] = useState({});

    const openModal = (item) => {
        setOpeningItem(item);
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setOpeningItem({});
    };

    const handleOperation = () => {
        closeModal();
        fetchData(`?search=${searchText}&payment_status=${paymentFilterStatus}&payment_date=${filterDate}`);
        fetchDataStats();
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSearch = (query) => {
        setSearchText(query);
        fetchData(`?search=${query}&payment_date=${filterDate}&payment_status=${paymentFilterStatus}`);
    };

    const handleDateChange = (value) => {
        setFilterDate(value);
        fetchData(`?search=${searchText}&payment_date=${value}&payment_status=${paymentFilterStatus}`);
    };

    const handlePaymentStatusChange = (value) => {
        setPaymentFilterStatus(value);
        fetchData(`?search=${searchText}&payment_status=${value}&payment_date=${filterDate}`);
    };

    const resetFilters = () => {
        setSearchText("");
        setFilterDate("");
        setPaymentFilterStatus("");
        fetchData("?payment_status=COMPLETED");
    };

    const fetchData = async (query) => {
        setLoading(true);
        try {
            let res = await getPayments(query);
            if (res.status === 200) {
                const data = res.data;
                setData(data);
            } else {
                toast.error("Unable to fetch Data");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDataStats = async () => {
        try {
            let res = await getPaymentStats();
            if (res.status === 200) {
                const data = res.data;
                setCardData([
                    {
                        title: "Total Completed Payments",
                        count: data.total_completed_payments,
                    },
                    {
                        title: "SOON",
                        count: "Comming Soon",
                    },
                ]);
                setGraphData(data.total_completed_month_wise);
            } else {
                toast.error("Unable to fetch Stats");
            }
        } catch (error) {
            console.error("Error:", error);
        }
    };

    useEffect(() => {
        fetchData(`?payment_status=COMPLETED&payment_date=${filterDate}`);
        fetchDataStats();
    }, []);

    const copyClipboard = (text) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                toast.success('Copied to clipboard!');
            })
            .catch(() => {
                toast.error('Failed to copy');
            });
    };
    return (<>
        <Breadcrumbs items={[
            { name: 'Home', link: '/dashboard' },
            { name: 'Sales', },
            { name: 'Payments' },
        ]} />
        <div className="py-3">
            <StyledHeadingText text={"Shipments"} />
            <div className="flex flex-col lg:flex-row gap-3 items-start">
                <div className="flex flex-col gap-2 justify-start items-start">
                    {cardData.map((card, index) => (
                        <Card title={card.title} count={card.count} />
                    ))}
                </div>
                <div className="w-full lg:w-10/12 bg-white rounded-lg border p-4">
                    <BarChart data={graphData} />
                </div>
            </div>
        </div>
        <div className="flex flex-row justify-between item-center">
            <div className="flex flex-row items-center gap-2">
                <SearchBar
                    placeholder="Search for Name | Order"
                    onSearch={handleSearch}
                    value={searchText}
                />

                <Dropdown
                    name="status"
                    options={[
                        { label: "Completed", value: "COMPLETED" },
                        { label: "Pending", value: "PENDING" },
                        { label: "Failed", value: "FAILED" },
                    ]}
                    value={paymentFilterStatus}
                    placeholder={"Payment Status"}
                    onChange={handlePaymentStatusChange}
                />

                {/* Date Picker */}
                <DatePicker
                    value={filterDate}
                    onChange={handleDateChange}
                    placeholder="Date"
                />
                {/* Reset Filters Icon */}
                {(searchText.length !== 0 || filterDate.length !== 0 || paymentFilterStatus.length !== 0) && (
                    <BiRefresh
                        size={80}
                        className="cursor-pointer text-primary"
                        onClick={resetFilters}
                    />
                )}
            </div>
        </div >
        {
            loading ?
                <div className="flex w-full flex-row justify-center my-2">
                    < CustomLoading size={100} />
                </div >
                :
                <div className="mt-2">
                    <Table
                        headers={headers}
                        rows={data?.results?.map((item, index) => (
                            {
                                created_at: <p>{formatDateTime(item.created_at)}</p>,
                                farmer_mobile_number: <p>{item.farmer_mobile_number}</p>,
                                farmer_name: <p>{item.farmer_name}</p>,
                                order: <span className='flex flex-row gap-1 items-center'><p className="max-w-16 truncate cursor-help" title={item.order}>{item.order}</p> <BiCopy
                                    className='cursor-pointer'
                                    color='blue'
                                    onClick={() => { copyClipboard(item.order) }}
                                /></span>,
                                payment_amount: <p>₹{item.payment_amount}</p>,
                                payment_method: <p>{item.payment_method}</p>,
                                payment_status: <p>{item.payment_status}</p>,
                                ...(getUserRole() === "admin" && {
                                    action: (
                                        <div className="flex gap-2 justify-center">
                                            <BiEdit
                                                size={20}
                                                className="cursor-pointer text-blue-500 hover:text-blue-700"
                                                onClick={() => openModal(item)}
                                                title="Edit Payment"
                                            />
                                        </div>
                                    ),
                                })
                            }))}
                    />
                </div>
        }
        {/* Table End */}
        {/* Pagination Begin */}
        {
            data.count !== 0 ?
                <Pagination
                    currentPage={currentPage}
                    
                    pageSize={pageSize}
                    totalEntries={data?.count}
                    onPageChange={handlePageChange}
                    data={data}
                    setData={setData}
                /> : <></>
        }

        <ShowModal
            isOpen={modalIsOpen}
            onRequestClose={closeModal}
            contentLabel="Edit Payment"
            modalWidth="max-w-3xl"
            componentToPass={<EditPayment item={openingItem} onOperation={handleOperation} />}
        />
    </>);
}

export default Payments;